import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { isAddressValid } from '@/components/AddressForm';
import { DiscardChangesDialog } from '@/components/DiscardChangesDialog/DiscardChangesDialog';
import {
  PageLayout,
  PageLayoutContent,
  PageLayoutLeftPart,
  PageLayoutLeftPartContent,
  PageLayoutSimpleHeader,
} from '@/components/PageLayout';
import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button';
import { Footer, FooterButtons } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import {
  articleHasQuoteAnalysisRefused,
  hasQuoteRefusedByTheClient,
  isMissingCustomsData,
} from '@/models/article';
import { getEntityShippingAddress } from '@/models/entity';
import { RequestWithRelations, useRequest } from '@/models/request';
import {
  ShipmentCarrierService,
  ShipmentHandover,
  useCreateShipment,
  useShipments,
} from '@/models/shipment';
import { canCreateShipments } from '@/models/user';
import ShipmentForm from '@/routes/Shipments/Shipment/components/ShipmentForm';
import { useCurrentSession } from '@/services/auth';
import { useShowWarningBeforeLeavingApp, useSimpleBlocker } from '@/utils/navigation';
import useViewPort from '@/utils/useViewport';

import { ShipmentFormArticle } from '../Shipment/components/ShipmentForm/components/ArticleSelect/ArticleSelect';
import {
  newShipmentFormPlace,
  ShipmentFormPlace,
} from '../Shipment/components/ShipmentForm/components/PlaceSelect/PlaceSelect';

const New = () => {
  const blocker = useSimpleBlocker();

  useShowWarningBeforeLeavingApp();

  return (
    <>
      <NewDrawerWrapper />
      {blocker.state === 'blocked' && (
        <DiscardChangesDialog
          onConfirm={() => blocker.proceed()}
          onCancel={() => blocker.reset()}
        />
      )}
    </>
  );
};

const NewDrawerWrapper = () => {
  const { state } = useLocation();
  const [requestId] = useState(state?.requestId as string);
  const [articleId] = useState(state?.articleId as string);
  const { currentSession } = useCurrentSession();
  const navigate = useNavigate();

  const { data: request, isLoading: isLoadingRequest } = useRequest(requestId);
  const { data: { shipments } = { shipments: [] }, isLoading: isLoadingShipments } = useShipments(
    {
      requestId,
      steps: ['preparation', 'finalisation', 'handover', 'in-transit', 'verification'],
    },
    {
      enabled: !!requestId,
    }
  );

  const initialArticle = request?.articles.find((article) => article.id === articleId);

  // Ignore store-to-client with pickup & transits from the client
  const isInitialArticleReadyForTransit =
    initialArticle?.step &&
    initialArticle.step.step === 'transit' &&
    !(
      !!initialArticle.atEntity &&
      initialArticle.atEntityId === request?.storeId &&
      initialArticle.toClient &&
      initialArticle.step.config.clientPickup
    ) &&
    !initialArticle.atClient &&
    !!initialArticle.atEntityId;

  if (!!currentSession && !canCreateShipments(currentSession)) {
    navigate('/shipments', { state: { skipRouterBlocker: true }, replace: true });

    return null;
  }

  if (!requestId) {
    return <NewDrawer />;
  }

  if (isLoadingRequest) {
    return null;
  }

  if (!request || !initialArticle || !isInitialArticleReadyForTransit) {
    navigate('/shipments', { state: { skipRouterBlocker: true }, replace: true });

    return null;
  }

  const { origin, destination } = getArticleOriginDestination(request, initialArticle);

  if (!origin || !destination) {
    navigate('/shipments', { state: { skipRouterBlocker: true }, replace: true });

    return null;
  }

  if (isLoadingShipments) {
    return null;
  }

  const articlesReadyForShipmentWithSameOriginDestination = request.articles
    .filter((article) => {
      const isAlreadyInAnotherShipment = shipments.some((shipment) => {
        return shipment.articles.some((shipmentArticle) => {
          return shipmentArticle.articleId === article.id;
        });
      });

      const isMissingCustoms =
        article.areCustomsExpected &&
        isMissingCustomsData(article, request.organization.config.customs?.type ?? 'fields');

      const { origin: artOrigin, destination: artDestination } = getArticleOriginDestination(
        request,
        article
      );

      const isDoingTheSameTransit =
        article.step!.id === initialArticle.step!.id &&
        !!article.atEntity &&
        artOrigin?.id === origin.id &&
        artDestination?.id === destination.id;

      return !isAlreadyInAnotherShipment && !isMissingCustoms && isDoingTheSameTransit;
    })
    .map((article) => ({
      ...article,
      request,
      organization: request.organization,
      quoteRefusedByTheClient: hasQuoteRefusedByTheClient(article),
      quoteRefusedDuringAnalysis: articleHasQuoteAnalysisRefused(article),
    }));

  return (
    <NewDrawer
      initialOrigin={origin}
      initialDestination={destination}
      initialArticles={articlesReadyForShipmentWithSameOriginDestination}
    />
  );
};

function getArticleOriginDestination(
  request: RequestWithRelations,
  article: RequestWithRelations['articles'][number]
) {
  const { client } = request;
  const { atEntity, toEntity } = article;

  const originType = article.atClient ? 'client' : 'entity';
  const origin = {
    client: client?.address ? newShipmentFormPlace(client.address, client, null) : null,
    entity: getEntityShippingAddress(atEntity)
      ? newShipmentFormPlace(getEntityShippingAddress(atEntity)!, null, atEntity)
      : null,
  }[originType];

  const destinationType = article.toClient ? 'client' : 'entity';
  const destination = {
    client: client?.address ? newShipmentFormPlace(client.address, client, null) : null,
    entity: getEntityShippingAddress(toEntity)
      ? newShipmentFormPlace(getEntityShippingAddress(toEntity)!, null, toEntity)
      : null,
  }[destinationType];

  return { origin, destination };
}

const NewDrawer = ({
  initialOrigin,
  initialDestination,
  initialArticles = [],
}: {
  initialOrigin?: ShipmentFormPlace;
  initialDestination?: ShipmentFormPlace;
  initialArticles?: ShipmentFormArticle[];
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const navigate = useNavigate();

  const [origin, setOrigin] = useState<ShipmentFormPlace | undefined>(initialOrigin);
  const [destination, setDestination] = useState<ShipmentFormPlace | undefined>(initialDestination);
  const [articles, setArticles] = useState<ShipmentFormArticle[]>(initialArticles);
  const [carrierService, setCarrierService] = useState<ShipmentCarrierService>();
  const [handover, setHandover] = useState<ShipmentHandover>();
  const [pickupDate, setPickupDate] = useState<string>();
  const [trackingId, setTrackingId] = useState<string>();
  const [error, setError] = useState<string>();

  const { mutateAsync: createShipment, isPending: isCreateShipmentPending } = useCreateShipment();

  const handleError = (error: string) => {
    setError(error);
    window.scroll({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
  };

  const onSubmit = () => {
    if (!origin) {
      handleError(t({ id: 'shipments.new.error.origin', message: 'Please select an origin' }));

      return;
    }

    if (!destination) {
      handleError(
        t({ id: 'shipments.new.error.destination', message: 'Please select a destination' })
      );

      return;
    }

    if (articles.length === 0) {
      handleError(
        t({ id: 'shipments.new.error.articles', message: 'Please add at least one item' })
      );

      return;
    }

    if (!carrierService) {
      handleError(t({ id: 'shipments.new.error.carrier', message: 'Please select a carrier' }));

      return;
    }

    const isNotPrivateCarrier = carrierService !== 'private-carrier';

    if (!!carrierService && isNotPrivateCarrier && !handover) {
      handleError(
        t({
          id: 'shipments.new.error.handover',
          message: 'Please select a handover option',
        })
      );

      return;
    }

    if (handover === 'pickup' && isNotPrivateCarrier && !pickupDate) {
      handleError(
        t({
          id: 'shipments.new.error.pickup-date',
          message: 'Please select a pickup date',
        })
      );

      return;
    }

    if (!isAddressValid(destination)) {
      handleError(
        t({
          id: 'shipments.new.error.invalid-destination',
          message: 'Please select a valid destination',
        })
      );

      return;
    }

    createShipment({
      origin: origin.client?.id ?? origin.entity?.id ?? '',
      destination: destination.client?.id ?? destination.entity?.id ?? '',
      articles: articles.map((article) => article.id),
      carrierService,
      handover: carrierService === 'private-carrier' ? 'pickup' : handover,
      pickupDate:
        handover === 'pickup' && carrierService !== 'private-carrier' ? pickupDate : undefined,
      trackingId: carrierService === 'private-carrier' ? trackingId : undefined,
    })
      .then((shipment) => {
        navigate(`/shipments/${shipment.id}`, {
          state: { skipRouterBlocker: true },
          replace: true,
        });
      })
      .catch((err) => {
        console.error(err);
        setError(
          (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
        );
      });
  };

  return (
    <PageLayout>
      <PageLayoutSimpleHeader
        onClose={() =>
          navigate('/shipments', { state: { skipRouterBlocker: false }, replace: true })
        }
        title={t({ id: 'shipments.new.title', message: 'Create a new delivery' })}
      />
      <PageLayoutContent>
        <PageLayoutLeftPart>
          <PageLayoutLeftPartContent>
            <Stack padding={isMobile ? '1rem' : undefined} gap="1rem">
              {!!error && (
                <AlertBar
                  type="error"
                  title={t({
                    id: 'shipments.new.error',
                    message:
                      'Please fill the missing information in order to be able to create a shipment',
                  })}
                />
              )}
              <ShipmentForm
                {...{
                  origin,
                  setOrigin,
                  destination,
                  setDestination,
                  articles,
                  setArticles,
                  carrierService,
                  setCarrierService,
                  handover,
                  setHandover,
                  pickupDate,
                  setPickupDate,
                  trackingId,
                  setTrackingId,
                  error,
                }}
              />
            </Stack>
          </PageLayoutLeftPartContent>

          <Footer>
            <FooterButtons>
              <Button variant="primary" onPress={onSubmit} isLoading={isCreateShipmentPending}>
                <Trans id="shipments.new.create">Create a shipment</Trans>
              </Button>
            </FooterButtons>
          </Footer>
        </PageLayoutLeftPart>
      </PageLayoutContent>
    </PageLayout>
  );
};

export default New;
